import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import './App.css';
import LoginScreen from './components/LoginScreen';
import LandingPage from './components/LandingPage';
import LegalPage from './components/LegalPage';
import PublicPage from './components/PublicPage';
import CallWithoutLoginScreen from './components/CallWithoutLoginScreen';
import ChatWindow from './components/chat/ChatWindow';
import Gallery from './components/Gallery';
import BottomNav, { PageType } from './components/shared/BottomNav';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import axiosInstance from './api/axiosInstance';
import { API_CONFIG } from './config';
import { getStoredToken } from './utils/authStorage';

export interface FriendEntry {
  id: number;
  name: string;
  conversationId?: number;
  avatarUrl?: string | null;
  isOnline: boolean;
  lastSeenIst: string;
  unreadCount: number;
}

interface FriendApiEntry {
  id?: number;
  name?: string;
  userId?: number;
  userName?: string;
  conversationId?: number;
  avatarUrl?: string | null;
  isOnline?: boolean;
  lastSeenIst?: string;
  unreadCount?: number;
}

function AppContent() {
  const { user, logout, loading } = useAuth();
  const [currentPage, setCurrentPage] = useState<PageType>('chat');
  const [friends, setFriends] = useState<FriendEntry[]>([]);
  const [selectedFriend, setSelectedFriend] = useState<FriendEntry | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteSenderName, setInviteSenderName] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) {
      setInviteToken(t);
      axiosInstance
        .get<{ valid: boolean; senderName?: string; receiverEmail?: string }>('/api/invite/validate', { params: { token: t } })
        .then((r) => {
          if (r.data?.valid && r.data.senderName) setInviteSenderName(r.data.senderName);
        })
        .catch(() => {});
    }
  }, []);

  const loadFriends = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axiosInstance.get<FriendApiEntry[]>('/api/friends');
      const normalized = Array.isArray(data)
        ? data
            .map((f) => ({
              id: f.id ?? f.userId ?? 0,
              name: f.name ?? f.userName ?? 'Unknown User',
              conversationId: f.conversationId,
              avatarUrl: f.avatarUrl ?? null,
              isOnline: f.isOnline ?? false,
              lastSeenIst: f.lastSeenIst ?? '',
              unreadCount: f.unreadCount ?? 0,
            }))
            .filter((f) => f.id > 0)
        : [];

      setFriends(normalized);
      setSelectedFriend((current) => {
        if (!current) return null;
        return normalized.find((f) => f.id === current.id) ?? current;
      });
    } catch {
      setFriends([]);
    }
  }, [user]);

  useEffect(() => {
    if (user) loadFriends();
  }, [user, loadFriends]);

  const handleSelectFriend = useCallback((friend: FriendEntry | null) => {
    if (!friend) {
      setSelectedFriend(null);
      loadFriends();
      return;
    }

    const selected = { ...friend, unreadCount: 0 };
    setSelectedFriend(selected);
    setFriends((current) =>
      current.map((item) => (item.id === friend.id ? { ...item, unreadCount: 0 } : item))
    );
  }, [loadFriends]);

  useEffect(() => {
    if (!user) return;

    const token = getStoredToken();
    const url = token
      ? `${API_CONFIG.chatHubURL}?access_token=${encodeURIComponent(token)}`
      : `${API_CONFIG.chatHubURL}?userId=${user.id}`;

    const presenceConnection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        skipNegotiation: false,
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .build();

    presenceConnection.on('UserStatusChanged', (userId: string, isOnline: boolean, lastSeenUtc: string) => {
      const changedUserId = Number(userId);
      const lastSeenIst = lastSeenUtc
        ? new Date(lastSeenUtc).toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).replace(' ', 'T')
        : '';
      setFriends((current) =>
        current.map((friend) =>
          friend.id === changedUserId
            ? { ...friend, isOnline, lastSeenIst: isOnline ? friend.lastSeenIst : lastSeenIst }
            : friend
        )
      );
      setSelectedFriend((current) =>
        current?.id === changedUserId
          ? { ...current, isOnline, lastSeenIst: isOnline ? current.lastSeenIst : lastSeenIst }
          : current
      );
    });

    presenceConnection.on('FriendsChanged', () => {
      loadFriends();
    });

    presenceConnection
      .start()
      .then(() => presenceConnection.invoke('RegisterUser', user.id))
      .catch((error) => {
        console.error('Presence connection failed:', error);
      });

    presenceConnection.onreconnected(() => {
      presenceConnection.invoke('RegisterUser', user.id).catch((error) => {
        console.error('Presence re-register failed:', error);
      });
      loadFriends();
    });

    return () => {
      presenceConnection.stop().catch(() => {});
    };
  }, [loadFriends, user]);

  const handleLogout = useCallback(async () => {
    await logout();
    setSelectedFriend(null);
    setFriends([]);
    setCurrentPage('chat');
  }, [logout]);

  const renderPage = () => {
    if (!user) return null;
    switch (currentPage) {
      case 'chat':
        return (
          <ChatWindow
            currentUserId={String(user.id)}
            currentUserName={user.name}
            selectedFriend={selectedFriend}
            friends={friends}
            onSelectFriend={handleSelectFriend}
            onFriendsRefresh={loadFriends}
            onLogout={handleLogout}
          />
        );
      case 'gallery':
        return <Gallery currentUserId={String(user.id)} selectedFriend={selectedFriend} />;
      default:
        return null;
    }
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <LoginScreen
        inviteToken={inviteToken}
        inviteSenderName={inviteSenderName}
        onLogin={() => {}}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="relative h-screen flex flex-col">
        <div className="flex-1 overflow-hidden">{renderPage()}</div>
        {(currentPage !== 'chat' || selectedFriend) && (
          <BottomNav currentPage={currentPage} onPageChange={setCurrentPage} />
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/features" element={<PublicPage slug="features" />} />
        <Route path="/privacy" element={<PublicPage slug="privacy" />} />
        <Route path="/how-it-works" element={<PublicPage slug="how-it-works" />} />
        <Route path="/silent-messaging" element={<PublicPage slug="silent-messaging" />} />
        <Route path="/browser-video-calling" element={<PublicPage slug="browser-video-calling" />} />
        <Route path="/about" element={<PublicPage slug="about" />} />
        <Route path="/blog" element={<PublicPage slug="blog" />} />
        <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
        <Route path="/terms-of-service" element={<LegalPage type="terms" />} />
        <Route path="/login" element={<AppContent />} />
        <Route path="/invite" element={<AppContent />} />
        <Route path="/call-without-login" element={<CallWithoutLoginScreen />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
